/**
 * SortableBenefitLine
 * A single drag-to-reorder benefit line for the Booking settings "Benefits Card"
 * editor. Follows the @dnd-kit/sortable pattern used by ArrayItemCard.
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface SortableBenefitLineProps {
  id: string
  value: string
  placeholder: string
  onChange: (value: string) => void
  onRemove: () => void
}

export function SortableBenefitLine({
  id,
  value,
  placeholder,
  onChange,
  onRemove,
}: SortableBenefitLineProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex items-center gap-2', isDragging && 'opacity-50 z-50')}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none shrink-0 text-muted-foreground"
        aria-label="Drag to reorder benefit line"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove benefit line"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}
